"use client";

import { ReactNode, useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";

/**
 * Scroll-linked section reveal that guarantees visibility.
 * Animation completes EARLY (by 30-40% scroll progress) so content
 * is always fully visible when the user reaches it.
 */
interface ScrollRevealSectionProps {
    children: ReactNode;
    className?: string;
    variant?: "fade-up" | "zoom" | "slide-left" | "slide-right";
}

export function ScrollRevealSection({
    children,
    className,
    variant = "fade-up",
}: ScrollRevealSectionProps) {
    const ref = useRef<HTMLDivElement>(null);

    const { scrollYProgress } = useScroll({
        target: ref,
        // Start when bottom of viewport reaches top of element,
        // end when element is 30% into the viewport — animation completes early
        offset: ["start end", "start 0.6"],
    });

    const spring = { stiffness: 120, damping: 30 };

    // All values go from "hidden" to "visible" in the range [0, 1]
    // Since offset ends at "start 0.6", animation is done by the time
    // the section is 40% into viewport — no more blurry content.
    const opacity = useSpring(
        useTransform(scrollYProgress, [0, 0.6, 1], [0, 0.7, 1]),
        spring
    );

    const y = useSpring(
        useTransform(scrollYProgress, [0, 1], [variant === "fade-up" || variant === "zoom" ? 60 : 0, 0]),
        spring
    );

    const x = useSpring(
        useTransform(scrollYProgress, [0, 1], [
            variant === "slide-left" ? -80 : variant === "slide-right" ? 80 : 0,
            0,
        ]),
        spring
    );

    const scale = useSpring(
        useTransform(scrollYProgress, [0, 1], [variant === "zoom" ? 0.92 : 1, 1]),
        spring
    );

    return (
        <div ref={ref}>
            <motion.div
                style={{ opacity, y, x, scale }}
                className={className}
            >
                {children}
            </motion.div>
        </div>
    );
}
